import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ConfirmFertSlotRequestOrderModal } from "@/components/modal/ConfirmFertSlotRequestOrderModal";
import { ConfirmModal } from "@/components/modal/ConfirmModal";
import { NeedInfoModal } from "@/components/modal/NeedInfoModal";
import { RejectRequestModal } from "@/components/modal/RejectRequestModal";
import { ModuleTypes, MotiveTypes, RequestTypes } from "@/constants/enums";
import { SuppliesOrderStatus, SuppliesRequestStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import {
  getChoferes,
  getDepositos,
  getEquiposDisponibles,
} from "@/services/aux-entities.service";
import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { FuelSlotRequestActions } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestActions";
import { FuelSlotRequestMainData } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestMainData";
import { FuelSlotRequestMessagesTable } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestMessagesTable";
import {
  FuelSlotRequestOrdersTable,
  FuelSlotRequestOrdersTableItem,
} from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestOrdersTable";
import { FuelSlotRequestProductsTable } from "@/components/common/section-components/requests/fuel-slot-request/FuelSlotRequestProductsTable";
import { CancelMotiveCard } from "@/components/common/section-components/requests/CancelMotiveCard";
import {
  FuelSlotRequestDetail,
  FuelSlotRequestOrder,
  FuelSlotRequestProduct,
} from "./schema/fuel-slot-request-detail.schema";
import {
  confirmOrderHandler,
  fetchDetailHandler,
  fetchOrdersHandler,
  handleCreateOrderHandler,
  handleEditOrdersHandler,
  rejectHandler,
  cancelRequestHandler,
  cancelOrderHandler,
  reassignOrderHandler,
  sendInfoHandler,
  deliverOrderHandler,
  setDepositoHandler,
} from "./schema/handler-functions";
import { toaster } from "@/components/ui/toaster";
import { usePolling } from "@/hooks/usePolling";
import { PermissionCode } from "@/constants/permissions";
import { ActionCode } from "@/constants/action-codes";
import { useBlockedActions } from "@/hooks/useBlockedActions";
import {
  isSuppliesOrderEditable,
  isSuppliesOrderInTransit,
} from "@/utils/supplies-order-status";

const DetailLegend = ({ title }: { title: string }) => {
  return (
    <Flex alignItems="center" gap="30px">
      <Text fontWeight={700}>{title}</Text>
    </Flex>
  );
};

export const FuelSlotRequestDetailPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions, hasWriteAccess } = useRoles();
  const { isActionBlocked } = useBlockedActions(PermissionCode.FUEL_SLOTS_REQ, isActive);

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingSendInfo, setLoadingSendInfo] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [loadingCancelRequest, setLoadingCancelRequest] = useState(false);
  const [loadingConfirmOrderId, setLoadingConfirmOrderId] = useState<
    number | null
  >(null);
  const [loadingCancelOrderId, setLoadingCancelOrderId] = useState<
    number | null
  >(null);
  const [loadingDeliverOrderId, setLoadingDeliverOrderId] = useState<
    number | null
  >(null);

  const [detail, setDetail] = useState<FuelSlotRequestDetail | null>(null);
  const [orders, setOrders] = useState<FuelSlotRequestOrder[] | undefined>(
    undefined,
  );

  const { open: openInfoModal, setOpen: setOpenInfoModal } = useDisclosure();
  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();
  const {
    open: openCancelRequestModal,
    setOpen: setOpenCancelRequestModal,
  } = useDisclosure();

  const { open: openConfirmOrderModal, setOpen: setOpenConfirmOrderModal } =
    useDisclosure();
  const [selectedConfirmOrderId, setSelectedConfirmOrderId] = useState<
    number | null
  >(null);

  const { open: openCancelOrderModal, setOpen: setOpenCancelOrderModal } =
    useDisclosure();
  const [selectedCancelOrderId, setSelectedCancelOrderId] = useState<
    number | null
  >(null);

  const [openConfirmDeliverOrder, setOpenConfirmDeliverOrder] =
    useState<boolean>(false);
  const [selectedDeliverOrderId, setSelectedDeliverOrderId] = useState<
    number | null
  >(null);

  const [depositos, setDepositos] = useState<{ id: number; nombre: string }[]>(
    [],
  );
  const [loadingDepositos, setLoadingDepositos] = useState<boolean>(false);
  const [savingDeposito, setSavingDeposito] = useState<boolean>(false);

  const [driverOptions, setDriverOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [equipmentOptions, setEquipmentOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [loadingAuxOptions, setLoadingAuxOptions] = useState<boolean>(false);
  const [loadingEquipments, setLoadingEquipments] = useState<boolean>(false);

  const [openConfirmLastPending, setOpenConfirmLastPending] =
    useState<boolean>(false);
  const [pendingCancelOrderPayload, setPendingCancelOrderPayload] = useState<{
    orderId: number;
    body: { idMotivo: number; detalle: string };
  } | null>(null);

  const handleBack = () => {
    navigate(AppRoutes.FUEL_SLOTS_REQ);
  };

  const fetchDetail = async (showLoad = true) => {
    await fetchDetailHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingDetail,
      setDetail,
      showLoad,
    });
  };

  const fetchOrders = async (showLoad = true) => {
    await fetchOrdersHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingOrders,
      setOrders,
      showLoad,
    });
  };

  useEffect(() => {
    if (id && isActive) {
      fetchDetail();
      fetchOrders();
    }
  }, [id, isActive]);

  usePolling({
    callback: async () => {
      if (!loadingDetail) {
        await fetchDetail(false)
      }
    },
    isActive,
    delay: 10000
  });

  usePolling({
    callback: async () => {
      if (!loadingOrders) {
        await fetchOrders(false);
      }
    },
    isActive,
    delay: 15000
  })

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    const fetchAuxOptions = async () => {
      try {
        setLoadingAuxOptions(true);
        const drivers = await getChoferes();

        if (cancelled) return;

        setDriverOptions(
          (drivers ?? []).map((d) => ({
            label: d.nombreApellido,
            value: d.id,
          })),
        );

        setEquipmentOptions([]);
      } catch {
        if (cancelled) return;
        setDriverOptions([]);
        setEquipmentOptions([]);
        toaster.create({
          type: "error",
          title: "No se pudieron cargar los choferes.",
        });
      } finally {
        if (!cancelled) setLoadingAuxOptions(false);
      }
    };

    fetchAuxOptions();

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    const fetchDepositos = async () => {
      try {
        setLoadingDepositos(true);
        const response = await getDepositos();
        if (cancelled) return;
        setDepositos(response ?? []);
      } catch {
        if (cancelled) return;
        setDepositos([]);
      } finally {
        if (!cancelled) setLoadingDepositos(false);
      }
    };

    fetchDepositos();

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  const fetchEquiposByChoferAndDate = async (params: {
    driverId: number | null;
    deliveryDate: string | null;
  }) => {
    if (!params.driverId || !params.deliveryDate) {
      setEquipmentOptions([]);
      setLoadingEquipments(false);
      return;
    }

    try {
      setLoadingEquipments(true);
      const equipments = await getEquiposDisponibles({
        idChofer: params.driverId,
        fechaEntrega: params.deliveryDate,
        tipoModulo: ModuleTypes.Combustible
      });
      setEquipmentOptions(
        (equipments ?? []).map((e) => ({
          label: `${e.nombre}${e.patente ? " - " + e.patente : ""}`,
          value: e.id,
        })),
      );
    } catch {
      setEquipmentOptions([]);
      toaster.create({
        type: "error",
        title: "No se pudieron cargar los equipos.",
      });
    } finally {
      setLoadingEquipments(false);
    }
  };

  const productsTableItems =
    detail?.productos?.map((p: FuelSlotRequestProduct) => ({
      ...p,
      cantidad: `${new Intl.NumberFormat("es-AR").format(p?.cantidad ?? 0)}`,
    })) ?? [];

  const depositoItems = useMemo(() => {
    return (depositos ?? [])
      .filter((d) => Number.isFinite(Number(d?.id)))
      .map((d) => ({ label: String(d?.nombre ?? "-"), value: String(d?.id) }));
  }, [depositos]);

  // Solo se puede completar el depósito faltante mientras la solicitud no
  // esté en un estado final (Entregada/Rechazada/Cancelada) y el usuario
  // tenga permiso de escritura sobre la sección.
  const canSetDeposito =
    hasWriteAccess(PermissionCode.FUEL_SLOTS_REQ) &&
    detail?.estado !== SuppliesRequestStatus.DELIVERED &&
    detail?.estado !== SuppliesRequestStatus.REJECTED &&
    detail?.estado !== SuppliesRequestStatus.CANCELLED;

  const showDepositoSelector = !detail?.nombreDeposito && canSetDeposito;

  const handleSaveDeposito = async (idDeposito: number) => {
    await setDepositoHandler({
      idDeposito,
      id,
      navigate,
      fetchPermissions,
      setSavingDeposito,
      fetchDetail,
    });
  };

  const ordersTableItems: FuelSlotRequestOrdersTableItem[] = (orders ??
    []) as any;

  const handleSendInfo = async (event?: FieldValues) => {
    await sendInfoHandler({
      id,
      event,
      navigate,
      fetchPermissions,
      setLoadingSendInfo,
      setOpenInfoModal,
      fetchDetail,
    });
  };

  const handleReject = async (event?: FieldValues) => {
    await rejectHandler({
      id,
      event,
      navigate,
      fetchPermissions,
      setLoadingReject,
      setOpenRejectModal,
      fetchDetail,
    });
  };

  const handleCancelRequest = async (event?: FieldValues) => {
    await cancelRequestHandler({
      id,
      event,
      navigate,
      fetchPermissions,
      setLoadingCancelRequest,
      setOpenCancelRequestModal,
      fetchDetail,
      fetchOrders,
    });
  };

  const handleOpenCancelOrderModal = (orderId: number) => {
    setSelectedCancelOrderId(orderId);
    setOpenCancelOrderModal(true);
  };

  const executeCancelOrder = async (
    orderId: number,
    body: { idMotivo: number; detalle: string },
  ) => {
    await cancelOrderHandler({
      orderId,
      body,
      id,
      navigate,
      fetchPermissions,
      setLoadingCancelOrderId,
      fetchOrders,
    });

    setOpenCancelOrderModal(false);
    setSelectedCancelOrderId(null);
    fetchDetail(false);
  };

  const handleCancelOrderSubmit = async (event?: FieldValues) => {
    if (selectedCancelOrderId === null) return;

    const body = {
      idMotivo: Number(event?.message_motive?.[0]),
      detalle: event?.request_detail,
    };

    // Pedidos "en estado intermedio": Pendiente o No entregado, todavía
    // necesitan una acción. Si este es el último que queda en ese estado Y
    // ya hay otro pedido Entregado, cancelarlo deja la solicitud con todo
    // lo que iba a llegar resuelto (entregado o cancelado) -> avisamos antes
    // de ejecutar la cancelación. Si ningún pedido fue entregado, cancelar
    // el último no implica que la solicitud pase a "Entregada".
    const intermediateOrders = (orders ?? []).filter(
      (o: any) =>
        o?.estado === SuppliesOrderStatus.PENDING ||
        o?.estado === SuppliesOrderStatus.NOT_DELIVERED,
    );
    const isLastIntermediateOrder =
      intermediateOrders.length === 1 &&
      Number((intermediateOrders[0] as any)?.id ?? 0) ===
        Number(selectedCancelOrderId);
    const hasDeliveredOrder = (orders ?? []).some(
      (o: any) => o?.estado === SuppliesOrderStatus.DELIVERED,
    );

    if (isLastIntermediateOrder && hasDeliveredOrder) {
      setPendingCancelOrderPayload({ orderId: selectedCancelOrderId, body });
      setOpenCancelOrderModal(false);
      setOpenConfirmLastPending(true);
      return;
    }

    await executeCancelOrder(selectedCancelOrderId, body);
  };

  const handleAcceptConfirmLastPending = async () => {
    if (!pendingCancelOrderPayload) return;

    await executeCancelOrder(
      pendingCancelOrderPayload.orderId,
      pendingCancelOrderPayload.body,
    );

    setOpenConfirmLastPending(false);
    setPendingCancelOrderPayload(null);
  };

  const handleCreateOrder = () => {
    handleCreateOrderHandler(navigate, id);
  };

  const handleEditOrders = () => {
    handleEditOrdersHandler(navigate, id);
  };

  const handleOpenConfirmOrderModal = (orderId: number) => {
    setSelectedConfirmOrderId(orderId);
    setOpenConfirmOrderModal(true);
  };

  const handleConfirmOrderSubmit = async (payload: {
    orderId: number;
    driverId: number | null;
    equipmentId: number | null;
    deliveryDate: string;
    esMostrador: boolean;
  }) => {
    const currentOrder = (orders ?? []).find(
      (o: any) => Number(o?.id ?? 0) === Number(payload.orderId),
    ) as any;
    const canReassignNotDeliveredOrder =
      currentOrder?.estado === SuppliesOrderStatus.NOT_DELIVERED &&
      (currentOrder?.intentos ?? []).length >= 1;
    const canReassignAssignedOrder =
      (!!currentOrder?.chofer || !!currentOrder?.esMostrador) &&
      !isSuppliesOrderInTransit(currentOrder?.estado) &&
      currentOrder?.estado !== SuppliesOrderStatus.DELIVERED;
    const canReassignOrder =
      canReassignNotDeliveredOrder || canReassignAssignedOrder;

    const body = {
      fechaEntrega: payload.deliveryDate,
      idChofer: payload.esMostrador ? null : Number(payload.driverId),
      idEquipo: payload.esMostrador ? null : Number(payload.equipmentId),
      esMostrador: payload.esMostrador,
    };

    if (canReassignOrder) {
      await reassignOrderHandler({
        orderId: payload.orderId,
        body,
        id,
        navigate,
        fetchPermissions,
        setLoadingConfirmOrderId,
        fetchOrders,
      });

      fetchDetail(false);
      return;
    }

    await confirmOrderHandler({
      orderId: payload.orderId,
      body,
      id,
      navigate,
      fetchPermissions,
      setLoadingConfirmOrderId,
      fetchOrders,
    });

    fetchDetail(false);
  };

  const handleOpenConfirmDeliverOrder = (orderId: number) => {
    setSelectedDeliverOrderId(orderId);
    setOpenConfirmDeliverOrder(true);
  };

  const handleAcceptConfirmDeliverOrder = async () => {
    if (selectedDeliverOrderId === null) return;

    await deliverOrderHandler({
      orderId: selectedDeliverOrderId,
      id,
      navigate,
      fetchPermissions,
      setLoadingDeliverOrderId,
      fetchOrders,
    });

    setOpenConfirmDeliverOrder(false);
    setSelectedDeliverOrderId(null);
    fetchDetail(false);
  };

  const loading =
    loadingDetail ||
    loadingOrders ||
    loadingSendInfo ||
    loadingReject ||
    loadingConfirmOrderId !== null ||
    loadingDeliverOrderId !== null;

  const hasOrdersLoaded = (orders ?? []).length >= 1;
  const hasAnyOrderWithDriverAssigned = (orders ?? []).some(
    (o: any) => !!o?.chofer || !!o?.esMostrador,
  );

  const canCreateOrder =
    !isActionBlocked(ActionCode.CREATE_ORDERS) &&
    !hasOrdersLoaded &&
    (detail?.estado === SuppliesRequestStatus.PENDING ||
      detail?.estado === SuppliesRequestStatus.NEEDS_INFO);

  const canSendInfo =
    !isActionBlocked(ActionCode.REQUIRE_INFO) &&
    !hasAnyOrderWithDriverAssigned &&
    (detail?.estado === SuppliesRequestStatus.PENDING ||
      detail?.estado === SuppliesRequestStatus.NEEDS_INFO);

  // Una vez que se crean pedidos para la solicitud, Rechazar deja de estar
  // disponible: la única forma de cerrarla pasa a ser "Cancelar solicitud"
  // (ver canCancelRequest).
  const canReject =
    !isActionBlocked(ActionCode.REJECT_REQUEST) &&
    !hasOrdersLoaded &&
    (detail?.estado === SuppliesRequestStatus.PENDING ||
      detail?.estado === SuppliesRequestStatus.NEEDS_INFO);

  // El botón "Editar pedidos" solo se muestra si queda al menos un pedido que
  // la página de edición vaya a aceptar. Usa el mismo predicado que esa página
  // (isSuppliesOrderEditable) para que no se desalineen: En camino, Entregado
  // y Cancelado bloquean; Pendiente, Confirmado, No entregado y Rechazado no.
  const hasAnyEditableOrder = (orders ?? []).some((o: any) =>
    isSuppliesOrderEditable(o?.estado),
  );

  // Una vez que algún pedido salió a la calle o se entregó, la solicitud ya
  // está en curso: no se cancela ni admite pedidos nuevos.
  const hasOrderInFlightOrDelivered = (orders ?? []).some(
    (o: any) =>
      isSuppliesOrderInTransit(o?.estado) ||
      o?.estado === SuppliesOrderStatus.DELIVERED,
  );

  // La solicitud sigue abierta mientras no esté cerrada por estado.
  const isRequestOpen =
    detail?.estado !== SuppliesRequestStatus.CANCELLED &&
    detail?.estado !== SuppliesRequestStatus.REJECTED;

  // Con pedidos ya cargados "Crear pedido" desaparece (canCreateOrder exige
  // !hasOrdersLoaded), así que la única vía para sumar uno nuevo es "Agregar
  // pedido" dentro de la página de edición. Mientras no haya nada en la calle
  // ni entregado, ese camino tiene que seguir disponible aunque ningún pedido
  // existente sea editable: si no, una solicitud cuyo único pedido se canceló
  // queda sin salida (no puede crear, ni editar, ni rechazar: solo cancelarse).
  // No se gatea por estado de la solicitud porque el backend la deja en
  // "Pedido creado" al cancelar el único pedido, no la devuelve a "Pendiente".
  const canAddMoreOrders =
    hasOrdersLoaded && isRequestOpen && !hasOrderInFlightOrDelivered;

  // El botón "Editar pedidos" solo se oculta si la solicitud está Cancelada
  // o si no queda ningún pedido editable ni forma de agregar uno nuevo.
  const canEditOrders =
    !isActionBlocked(ActionCode.EDIT_ORDERS) &&
    (hasAnyEditableOrder || canAddMoreOrders) &&
    detail?.estado !== SuppliesRequestStatus.CANCELLED;

  // "Cancelar solicitud" requiere que ya existan pedidos (si no, se usa
  // Rechazar/Crear pedido, ver canReject/canCreateOrder) y que la solicitud
  // no esté ya cerrada. No se restringe a un listado fijo de estados: por
  // ejemplo "Pendiente" con pedidos ya cargados es el estado al que puede
  // volver la solicitud si se cancela el único pedido existente.
  const canCancelRequestStatus = hasOrdersLoaded && isRequestOpen;

  const canCancelRequest =
    !isActionBlocked(ActionCode.CANCEL_REQUEST) &&
    canCancelRequestStatus &&
    !hasOrderInFlightOrDelivered;

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Solicitud de combustible"
        onBack={handleBack}
        backButton
        loading={loading}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
          paddingRight="8px"
          paddingBottom="16px"
        >
          <FuelSlotRequestProductsTable
            items={productsTableItems}
            rightElement={
              <FuelSlotRequestActions
                showReject={canReject}
                showNeedsInfo={canSendInfo}
                showCreateOrder={canCreateOrder}
                showCancelRequest={canCancelRequest}
                loadingSendInfo={loadingSendInfo}
                loadingReject={loadingReject}
                loadingCancelRequest={loadingCancelRequest}
                setOpenInfoModal={setOpenInfoModal}
                setOpenRejectModal={setOpenRejectModal}
                setOpenCancelRequestModal={setOpenCancelRequestModal}
                handleCreateOrder={handleCreateOrder}
              />
            }
          />

          <FuelSlotRequestMainData
            fechaEntrega={detail?.fechaEntrega}
            titularCuenta={detail?.titularCuenta}
            destino={detail?.destino}
            nombreDeposito={detail?.nombreDeposito}
            showDepositoSelector={showDepositoSelector}
            depositoItems={depositoItems}
            loadingDepositos={loadingDepositos}
            savingDeposito={savingDeposito}
            onSaveDeposito={handleSaveDeposito}
            contacto={detail?.contacto}
            nombreContacto={detail?.nombreContacto}
            estadoDescripcion={detail?.estado}
          />

          {detail?.estado === SuppliesRequestStatus.CANCELLED &&
            detail?.motivoCancelacion && (
              <CancelMotiveCard
                motivo={detail?.motivoCancelacion}
                fecha={detail?.fechaCancelacion}
                usuario={detail?.usuarioCancela}
              />
            )}

          <DetailLegend title="Mensajes" />

          <Flex flexDir="column" flexShrink={0}>
            <FuelSlotRequestMessagesTable messages={detail?.mensajes || []} />
          </Flex>

          {Boolean(orders?.length) && (
            <FuelSlotRequestOrdersTable
              orders={ordersTableItems}
              loadingConfirmOrderId={loadingConfirmOrderId}
              onConfirmOrder={handleOpenConfirmOrderModal}
              showEditOrders={canEditOrders}
              onEditOrders={handleEditOrders}
              assignDriverBlocked={isActionBlocked(ActionCode.ASSIGN_DRIVER)}
              reassignDriverBlocked={isActionBlocked(ActionCode.REASSIGN_DRIVER)}
              onCancelOrder={handleOpenCancelOrderModal}
              loadingCancelOrderId={loadingCancelOrderId}
              cancelOrderBlocked={isActionBlocked(ActionCode.CANCEL_ORDER)}
              onDeliverOrder={handleOpenConfirmDeliverOrder}
              loadingDeliverOrderId={loadingDeliverOrderId}
              deliverOrderBlocked={isActionBlocked(ActionCode.DELIVER_ORDER)}
            />
          )}
        </Flex>
      )}

      {detail?.id && (
        <>
          <ConfirmModal
            openConfirm={openConfirmLastPending}
            setOpenConfirm={setOpenConfirmLastPending}
            handleAccept={handleAcceptConfirmLastPending}
            loadingAccept={
              pendingCancelOrderPayload
                ? loadingCancelOrderId === pendingCancelOrderPayload.orderId
                : false
            }
            title="Cancelar pedido"
            subtitle='Este es el último pedido pendiente de la solicitud. Al cancelarlo, la solicitud va a pasar a estado "Entregada".'
          />

          <ConfirmModal
            openConfirm={openConfirmDeliverOrder}
            setOpenConfirm={setOpenConfirmDeliverOrder}
            handleAccept={handleAcceptConfirmDeliverOrder}
            loadingAccept={loadingDeliverOrderId === selectedDeliverOrderId}
            title="Marcar como entregado"
            subtitle="Vas a marcar este pedido como entregado. Esta acción no se puede deshacer."
          />

          {selectedConfirmOrderId !== null && (
            <ConfirmFertSlotRequestOrderModal
              open={openConfirmOrderModal}
              setOpen={(flag) => {
                setOpenConfirmOrderModal(flag);
                if (!flag) setSelectedConfirmOrderId(null);
              }}
              orderId={selectedConfirmOrderId}
              loadingConfirm={loadingConfirmOrderId === selectedConfirmOrderId}
              titleText={(() => {
                const currentOrder = (orders ?? []).find(
                  (o: any) => Number(o?.id ?? 0) === Number(selectedConfirmOrderId),
                ) as any;

                const isPendingWithDriver =
                  currentOrder?.estado === SuppliesOrderStatus.PENDING &&
                  currentOrder?.chofer;

                const isNotDeliveredWithAttempts =
                  currentOrder?.estado === SuppliesOrderStatus.NOT_DELIVERED &&
                  (currentOrder?.intentos ?? []).length >= 1;
                const canReassignOrder = isNotDeliveredWithAttempts || isPendingWithDriver;
                return canReassignOrder ? "Reasignar pedido" : "Asignar pedido";
              })()}
              confirmButtonLabel={(() => {
                const currentOrder = (orders ?? []).find(
                  (o: any) => Number(o?.id ?? 0) === Number(selectedConfirmOrderId),
                ) as any;
                const isPendingWithDriver =
                  currentOrder?.estado === SuppliesOrderStatus.PENDING &&
                  currentOrder?.chofer;

                const isNotDeliveredWithAttempts =
                  currentOrder?.estado === SuppliesOrderStatus.NOT_DELIVERED &&
                  (currentOrder?.intentos ?? []).length >= 1;
                const canReassignOrder = isNotDeliveredWithAttempts || isPendingWithDriver;
                return canReassignOrder ? "Reasignar" : "Asignar";
              })()}
              defaultEsMostrador={(() => {
                const currentOrder = (orders ?? []).find(
                  (o: any) => Number(o?.id ?? 0) === Number(selectedConfirmOrderId),
                ) as any;
                return !!currentOrder?.esMostrador;
              })()}
              loadingDrivers={loadingAuxOptions}
              loadingEquipments={loadingAuxOptions || loadingEquipments}
              onDriverOrDateChange={fetchEquiposByChoferAndDate}
              handleConfirmOrder={handleConfirmOrderSubmit}
              driverOptions={driverOptions}
              equipmentOptions={equipmentOptions}
            />
          )}
          <NeedInfoModal
            open={openInfoModal}
            setOpen={setOpenInfoModal}
            handleAccept={handleSendInfo}
            loadingAccept={loadingSendInfo}
            idTipoSolicitud={RequestTypes.CuposCombustible}
          />
          <RejectRequestModal
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleReject}
            loadingAccept={loadingReject}
            idTipoSolicitud={RequestTypes.CuposCombustible}
            entityName="solicitud de combustible"
          />
          <RejectRequestModal
            open={openCancelRequestModal}
            setOpen={setOpenCancelRequestModal}
            handleAccept={handleCancelRequest}
            loadingAccept={loadingCancelRequest}
            idTipoSolicitud={RequestTypes.CuposCombustible}
            entityName="solicitud de combustible"
            isCancelling
            motiveType={MotiveTypes.CancelacionSolicitudInsumos}
          />
          {selectedCancelOrderId !== null && (
            <RejectRequestModal
              open={openCancelOrderModal}
              setOpen={(flag) => {
                setOpenCancelOrderModal(flag);
                if (!flag) setSelectedCancelOrderId(null);
              }}
              handleAccept={handleCancelOrderSubmit}
              loadingAccept={loadingCancelOrderId === selectedCancelOrderId}
              idTipoSolicitud={RequestTypes.PedidosInsumos}
              entityName="pedido"
              entityArticle="el"
              isCancelling
              motiveType={MotiveTypes.CancelacionPedidoInsumos}
            />
          )}
        </>
      )}
    </Flex>
  );
};
